import _ from 'lodash';
import constants from '$$helpers/constants';
import store from '$$globalStore';
import taskEventHandlers from './tasksEvents/eventHandler';
import columnEventHandlers from './columnsEvents/eventHandler';
import autoSchedulingPatchesModule from '../../../views/ganttView/autoScheduling';
import historyModel from '../../../models/history';

export default instance => {
  let unsavedChanges = {
    task: new Map(),
    link: new Map(),
    sourceEvent: null,
    actionHash: null,
    notFinishedUpdate: false,
  };
  let timeoutId;
  let stopManualAutoschedule = false;

  // Events from Store
  const storeSub = store.subscribe((mutation, state) => {
    if (mutation.payload?.fromGantt) {
      return;
    }

    if (mutation.type.includes('headerView/setCascadeSortingProcessing')) {
      if (mutation.payload) {
        instance.cascadeSort();
      }
    }

    if (mutation.type.includes('headerView/setExpandCollapseAllProcessing')) {
      if (mutation.payload.flag) {
        instance.expandCollapseAll(mutation.payload.type);
      }
    }

    if (mutation.type.includes('tasksModel')) {
      taskEventHandlers.eventsFromStore(mutation, instance);
    }

    // TODO need optimize by different resourcesModel mutations
    if (mutation.type.includes('resourcesModel')) {
      instance.callEvent('workloadClearAndParse');
    }

    if (mutation.type.includes('columns')) {
      columnEventHandlers.eventsFromStore(mutation, instance);
    }
  });

  instance.attachEvent('onCascadeSort', async tasks => {
    await store.dispatch('tasksModel/updateTasks', {
      tasks,
      sourceEvent: { type: constants.GANTT_SOURCE_EVENT_TYPES.CASCADE_SORT },
      fromGantt: false,
    });

    instance.render();
    store.commit('headerView/setCascadeSortingProcessing', false);
  });

  instance.attachEvent('onUpdateTasksCustomEstimations', async tasks => {
    await store.dispatch('tasksModel/updateTasks', {
      tasks,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.WORKLOAD_CUSTOM_ESTIMATION_UPDATE,
        sourceId: tasks?.[0]?.id,
      },
      fromGantt: false,
    });
  });

  instance.attachEvent('onMassUpdateTasks', async tasks => {
    await store.dispatch('tasksModel/updateTasks', {
      tasks,
      sourceEvent: { type: constants.GANTT_SOURCE_EVENT_TYPES.MASS_CHANGE },
      fromGantt: false,
    });
  });

  instance.attachEvent('onMassDeleteTasks', async tasks => {
    await store.dispatch('tasksModel/deleteTasks', {
      tasks,
      sourceEvent: { type: constants.GANTT_SOURCE_EVENT_TYPES.MASS_CHANGE },
      fromGantt: false,
    });
  });

  instance.attachEvent('onExpandCollapseAll', async (tasks, multiviewId) => {
    await store.dispatch('tasksModel/updateTasks', {
      tasks,
      multiviewId,
      sourceEvent: { type: constants.GANTT_SOURCE_EVENT_TYPES.EXPAND_COLLAPSE_ALL },
      fromGantt: true,
    });

    instance.render();
    store.commit('headerView/setExpandCollapseAllProcessing', false);
  });

  instance.attachEvent('onAfterTaskUpdate', (taskId, taskData) => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_UPDATE,
        sourceId: taskId,
      };
    }
  });
  instance.attachEvent('onAfterTaskAdd', (taskId, taskData) => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_CREATE,
        sourceId: taskId,
      };
    }
  });
  instance.attachEvent('onAfterTaskDelete', taskId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_DELETE,
        sourceId: taskId,
      };
    }
  });
  instance.attachEvent('onAfterTaskDrag', taskId => {
    //
  });
  instance.attachEvent('onAfterTaskMove', taskId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_MOVE,
        sourceId: taskId,
      };
    }
  });
  instance.attachEvent('onBeforeProjectMove', taskId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.PROJECT_TASK_MOVE,
        sourceId: taskId,
      };
    }
  });
  instance.attachEvent('onAfterLinkUpdate', linkId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.LINK_UPDATE,
        sourceId: linkId,
      };
    }
  });
  instance.attachEvent('onAfterLinkAdd', linkId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.LINK_CREATE,
        sourceId: linkId,
      };
    }
  });
  instance.attachEvent('onAfterlinkDelete', linkId => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.LINK_DELETE,
        sourceId: linkId,
      };
    }
  });
  instance.attachEvent('predecessorsUpdate', (taskId, insertLinks = [], deleteLinks = []) => {
    if (!unsavedChanges.sourceEvent) {
      unsavedChanges.sourceEvent = {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_PREDECESSOR_UPDATE,
        sourceId: taskId,
      };
    }

    instance.batchUpdate(() => {
      insertLinks.forEach(link => instance.addLink(link));
      deleteLinks.forEach(link => instance.deleteLink(link.id));
    });
  });
  instance.attachEvent('onAfterAutoSchedule', (taskId, updatedTasksIDs) => {
    const tasksToUpdate = autoSchedulingPatchesModule.prepareParentsForUpdate(taskId, updatedTasksIDs);

    // if (updatedTasksIDs.length) { // for correct dates
    //   updatedTasksIDs.forEach(id => {
    //     const task = instance.getTask(id);

    //     if (task) {
    //       tasksToUpdate.push(task);
    //     }
    //   });
    // }

    if (!tasksToUpdate.length) {
      return;
    }

    store.dispatch('tasksModel/updateTasks', {
      tasks: tasksToUpdate,
      ganttId: tasksToUpdate[0].gantt_id,
      fromGantt: true,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.AUTO_SCHEDULING,
        sourceId: tasksToUpdate[0].id,
      },
    });
  });

  // Tasks/Links events from Gantt
  const DP = instance.createDataProcessor((entity, action, data, id) => {
    console.log('GANTT EVENT', entity, action, data, id);
    if (data.type !== instance.config.types.button) {
      unsavedChanges[entity][+id] = {
        data,
        action,
      };

      if (entity === 'task') {
        const strToDate = instance.date.str_to_date(instance.config.date_format);

        Object.assign(unsavedChanges[entity][+id].data, {
          start_date: _.isString(data.start_date) ? strToDate(data.start_date) : data.start_date,
          end_date: _.isString(data.end_date) ? strToDate(data.end_date) : data.end_date,
          deadline: _.isString(data.deadline) && !_.isEmpty(data.deadline) ? strToDate(data.deadline) : data.deadline,
          constraint_date: null,
          constraint_type: null,
        });
      }
      unsavedChanges.notFinishedUpdate = false;
    }

    unsavedChanges.multiviewId = instance.config.multiview ? +instance.config.multiview_id : null;

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (instance.$destroyed) {
        return;
      }

      if (!stopManualAutoschedule) {
        let needAutoschedule = false;

        Object.entries(unsavedChanges.task).forEach(([key, unsavedTask]) => {
          if (!needAutoschedule && unsavedTask.action !== 'delete') {
            const constraintsGroup = instance.getConnectedGroup(unsavedTask.data.id);
    
            if (constraintsGroup.links.length) {
              needAutoschedule = true;
            }
          }
        });

        if (instance.config.auto_scheduling.enabled && needAutoschedule) {
          if (instance.$destroyed) {
            return;
          }

          instance.eachTask(task => {
            if (task.type !== instance.config.types.button && task.constraint_date) {
              task.constraint_date = null;
              task.constraint_type = null;
            }
          });
          unsavedChanges.notFinishedUpdate = true;// need for cases when autoSchedule() does not fires dataProcessor callback
          instance.autoSchedule();
          stopManualAutoschedule = true;

          if (unsavedChanges.notFinishedUpdate) {
            sendToUpdate();
          }
          
          return;
          // instance.render();
        }
      }

      sendToUpdate();
      stopManualAutoschedule = false;
    });
  });

  const sendToUpdate = () => {
    unsavedChanges.task = Object.entries(unsavedChanges.task).map(([key, value]) => value);
    unsavedChanges.link = Object.entries(unsavedChanges.link).map(([key, value]) => value);
    unsavedChanges.actionHash = historyModel.generateActionHash();
    
    taskEventHandlers.eventsFromGantt({ ...unsavedChanges }, instance);
    unsavedChanges = {
      task: {},
      link: {},
      sourceEvent: null,
      actionHash: null,
      notFinishedUpdate: false,
    };
  }

  // drop performance after autoscheduling mega updates
  DP.attachEvent('onRowMark', (id, state, mode, invalid) => false);

  return storeSub;
};
